/**
 * Work, power and efficiency.
 *
 *   W = F d cos θ      work done by a constant force F over a displacement d, θ being the
 *                      angle between the force and the displacement
 *   P = W / t          average power: work per unit time
 *   P = F v cos θ      instantaneous power of a force acting on something moving at speed v
 *   η = out / in       efficiency: the useful fraction of what goes in
 *
 * OpenStax University Physics Vol. 1, §7.1 (work), §7.4 (power). 1 W = 1 J/s. A
 * mechanical horsepower is 550 ft·lbf/s = 745.6999 W, a metric horsepower 735.49875 W,
 * and an electrical horsepower exactly 746 W (NIST SP 811, Appendix B.8).
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  ANGLE,
  EFFICIENCY,
  ENERGY,
  FORCE,
  LENGTH,
  POWER,
  SPEED,
  TIME,
  type UnitFamily,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

/** Smallest |cos θ| treated as non-zero: cos 90° is 6e-17 in floating point, not 0. */
const COS_EPSILON = 1e-12;

function arccosOf(ratio: number): number | RelationError {
  if (ratio > 1 + 1e-12 || ratio < -1 - 1e-12) {
    return fail("ratio-out-of-range");
  }
  return Math.acos(Math.min(1, Math.max(-1, ratio)));
}

export type WorkKey = "W" | "F" | "d" | "a";

export const WORK: RelationDef<WorkKey> = {
  keys: ["W", "F", "d", "a"],
  symbols: { W: "W", F: "F", d: "d", a: "θ" },
  families: { W: ENERGY, F: FORCE, d: LENGTH, a: ANGLE },
  equation: "W = F × d × cos(θ)",
  solvedFor: "W",
  rearranged: {
    W: "W = F × d × cos(θ)",
    F: "F = W ÷ (d × cos(θ))",
    d: "d = W ÷ (F × cos(θ))",
    a: "θ = arccos(W ÷ (F × d))",
  },
  workingUnit: { a: "deg" },
  solve(unknown, { W, F, d, a }) {
    if (unknown !== "F" && F < 0) {
      return fail("force-negative");
    }
    if (unknown !== "d" && d < 0) {
      return fail("distance-negative");
    }
    const cos = Math.cos(a);
    if (unknown === "W") {
      return F * d * cos;
    }
    if (unknown === "a") {
      return F > 0 && d > 0
        ? arccosOf(W / (F * d))
        : fail("force-distance-zero");
    }
    if (Math.abs(cos) < COS_EPSILON) {
      return fail("angle-right");
    }
    const other = unknown === "F" ? d : F;
    if (!(other > 0)) {
      return fail(unknown === "F" ? "distance-zero" : "force-zero");
    }
    const value = W / (other * cos);
    return value < 0 ? fail("sign-mismatch") : value;
  },
};

export type PowerWorkKey = "P" | "W" | "t";

export const POWER_WORK: RelationDef<PowerWorkKey> = {
  keys: ["P", "W", "t"],
  symbols: { P: "P", W: "W", t: "t" },
  families: { P: POWER, W: ENERGY, t: TIME },
  equation: "P = W ÷ t",
  solvedFor: "P",
  rearranged: { P: "P = W ÷ t", W: "W = P × t", t: "t = W ÷ P" },
  solve(unknown, { P, W, t }) {
    if (unknown !== "t" && t < 0) {
      return fail("time-negative");
    }
    if (unknown === "P") {
      return t > 0 ? W / t : fail("time-zero");
    }
    if (unknown === "W") {
      return P * t;
    }
    return P > 0 ? W / P : fail("power-zero");
  },
};

export type PowerForceKey = "P" | "F" | "v" | "a";

export const POWER_FORCE: RelationDef<PowerForceKey> = {
  keys: ["P", "F", "v", "a"],
  symbols: { P: "P", F: "F", v: "v", a: "θ" },
  families: { P: POWER, F: FORCE, v: SPEED, a: ANGLE },
  equation: "P = F × v × cos(θ)",
  solvedFor: "P",
  rearranged: {
    P: "P = F × v × cos(θ)",
    F: "F = P ÷ (v × cos(θ))",
    v: "v = P ÷ (F × cos(θ))",
    a: "θ = arccos(P ÷ (F × v))",
  },
  workingUnit: { a: "deg" },
  solve(unknown, { P, F, v, a }) {
    if (unknown !== "F" && F < 0) {
      return fail("force-negative");
    }
    if (unknown !== "v" && v < 0) {
      return fail("speed-negative");
    }
    const cos = Math.cos(a);
    if (unknown === "P") {
      return F * v * cos;
    }
    if (unknown === "a") {
      return F > 0 && v > 0 ? arccosOf(P / (F * v)) : fail("force-speed-zero");
    }
    if (Math.abs(cos) < COS_EPSILON) {
      return fail("angle-right");
    }
    const other = unknown === "F" ? v : F;
    if (!(other > 0)) {
      return fail(unknown === "F" ? "speed-zero" : "force-zero");
    }
    const value = P / (other * cos);
    return value < 0 ? fail("sign-mismatch") : value;
  },
};

export type EfficiencyKey = "eta" | "out" | "in";

/** Solve η = out ÷ in for one unknown; shared by the energy and power versions. */
function solveEfficiency(
  unknown: EfficiencyKey,
  { eta, out, in: input }: Record<EfficiencyKey, number>
): number | RelationError {
  if (unknown !== "in" && input < 0) {
    return fail("input-negative");
  }
  if (unknown !== "out" && out < 0) {
    return fail("output-negative");
  }
  if (unknown === "eta") {
    if (!(input > 0)) {
      return fail("input-zero");
    }
    return out > input * (1 + 1e-12) ? fail("over-unity") : out / input;
  }
  if (eta > 1 + 1e-12) {
    return fail("over-unity");
  }
  if (unknown === "out") {
    return eta < 0 ? fail("efficiency-negative") : eta * input;
  }
  return eta > 0 ? out / eta : fail("efficiency-zero");
}

function efficiencyRelation(family: UnitFamily): RelationDef<EfficiencyKey> {
  return {
    keys: ["eta", "out", "in"],
    symbols: { eta: "η", out: "out", in: "in" },
    families: { eta: EFFICIENCY, out: family, in: family },
    equation: "η = out ÷ in",
    solvedFor: "eta",
    rearranged: {
      eta: "η = out ÷ in",
      out: "out = η × in",
      in: "in = out ÷ η",
    },
    solve: solveEfficiency,
  };
}

/** Efficiency of an energy conversion (output and input in joules). */
export const EFFICIENCY_ENERGY = efficiencyRelation(ENERGY);
/** Efficiency of a power conversion (output and input in watts). */
export const EFFICIENCY_POWER = efficiencyRelation(POWER);
